import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { api as apiRequest } from '@culture/common-helpers/request';
import { filterQuery } from '@culture/common-helpers/url';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
	MetaPropType,
	NavigationPropType,
	PaginationPropType,
	PromoBlockPropType,
	RubricPropType,
	RubricTreePropType
} from 'propTypes/common';
import Container from 'components/Container';
import { Layout, LayoutBlock } from 'components/Layout';

import { useToggle } from 'hooks/useToogle';
import { useIsMobileWithBreakpoint } from 'hooks/useIsMobileWithBreakpoint';
import Rubricator from 'components/filters/Rubricator';
import CatalogTitleHeading from 'components/CatalogTitleHeading';
import { withYMapsContext } from 'hocs/withYMapsContext';
import Preloader from 'components/Preloader';
import SectionCategories from 'components/catalogs/SectionCategories';
import MapSwitchButton from 'components/catalogs/MapSwitchButton';
import Catalog from './components/Catalog';
import Filters from './components/Filters';

import styles from './styles.module.scss';

import { filtersValuesPropType } from './propTypes';

const Map = dynamic(() => import('components/catalogs/Map'), {
	ssr: false,
	loading: () => <Preloader height="400px" />
});

const getAtlasInstitutes = (total, rubricId, urlParams) => {
	const { subordinationType, isCommercial } = urlParams;

	const params = filterQuery({
		statuses: ['published'],
		rubricPaths: [rubricId],
		limit: total || 1,
		..._(urlParams).pick(
			'location',
			'architect',
			'hasInfographic',
			'hasVirtualTours',
			'isModelLibrary',
			'type',
			'query'
		),
		subordinationTypes: [subordinationType],
		...(!!isCommercial && {
			organizationTypes: ['commercial']
		})
	});

	return apiRequest('atlas/institutes', params);
};

const InstitutesTemplate = ({
	defaultUrlParams,
	institutes,
	menu,
	meta,
	urlParams,
	rootRubricPath,
	federalSubjects,
	topPromoBlock,
	rubric,
	rubricsTree,
	blocks,
	rubricName,
	filtersValues
}) => {
	const { push } = useRouter();
	const isMobile = useIsMobileWithBreakpoint('tabletSmall');
	const [isMapOpen, openMap, closeMap] = useToggle(false);
	const [mapItems, setMapItems] = useState([]);
	const [mapItemsLoading, setItemsLoading] = useState(false);

	const catalogContainerRef = useRef(null);
	const contentContainerRef = useRef(null);

	const { total } = institutes;

	const toggleMap = () => {
		if (isMapOpen) {
			closeMap();
			if (!_.isEmpty(mapItems) && isMobile) {
				contentContainerRef.current.scrollIntoView({ behavior: 'smooth' });
			}
		} else {
			if (!_.isEmpty(mapItems)) {
				catalogContainerRef.current.scrollIntoView({ behavior: 'smooth' });
			}
			openMap();
		}
	};

	const currentSelectedRubricPath = rubric.path.replace(rootRubricPath, '');

	const changeUrl = ({ ...params }) => {
		const { location, rubricPath: path, ...queryParams } = filterQuery(params);

		const locationPath = `location-${params.location || 'russia'}`;

		let urlPage = `/${rootRubricPath}/${locationPath}`;

		if (currentSelectedRubricPath && !path) {
			urlPage += currentSelectedRubricPath;
		}

		if (path) {
			urlPage += path;
		}

		if (params.hasInfographic) {
			urlPage += '/hasInfographic-true';
		}

		if (params.hasVirtualTours) {
			urlPage += '/hasVirtualTours-true';
		}

		if (params.isCommercial) {
			urlPage += '/isCommercial-true';
		}

		if (params?.architect) {
			urlPage += `/architect-${params?.architect}`;
		}

		if (params?.subordinationType) {
			urlPage += `/subordinationType-${params?.subordinationType}`;
		}

		if (params?.isModelLibrary) {
			urlPage += '/isModelLibrary-true';
		}

		if (params?.type) {
			urlPage += `/type-${params.type}`;
		}

		push({
			pathname: urlPage,
			query: _(queryParams).pick('query', 'sort', 'cleanCache')
		});
	};

	const handleFilterChange = useCallback(
		(name, value) => {
			const currentValue = urlParams[name] || '';
			if (value !== currentValue) {
				const params = {
					...urlParams,
					[name]: value,
					page: null
				};

				changeUrl(params);
			}
		},
		[urlParams]
	);

	useEffect(() => {
		const getAllItems = async (totalItems, rubricId, params) => {
			setItemsLoading(true);
			const mapInstitutes = await getAtlasInstitutes(
				totalItems,
				rubricId,
				params
			);
			setMapItems(mapInstitutes);
			setItemsLoading(false);
		};

		getAllItems(total, rubric._id, urlParams);
	}, [rubric]);

	const handleFilterReset = () => {
		const resetPath = `/${rootRubricPath}/location-russia${currentSelectedRubricPath}`;
		push({ pathname: resetPath });
	};

	const emptyMessage = 'По вашему запросу ничего не найдено';

	return (
		<Layout {...{ menu, meta, topPromoBlock }}>
			<SectionCategories
				blocks={blocks}
				federalSubjects={federalSubjects}
				rubricName={rubricName}
			/>
			<LayoutBlock
				gutter="mediumConstant"
				ref={contentContainerRef}
				className={styles.Content}
			>
				<Container innerClassName={styles.Title}>
					<CatalogTitleHeading
						{...{ headingTitle: meta?.headingTitle, total }}
					/>
					<MapSwitchButton {...{ isMapOpen, toggleMap }} />
				</Container>
			</LayoutBlock>
			<LayoutBlock gutter="mediumConstant">
				<Container>
					<Filters
						{...{
							urlParams,
							defaultUrlParams,
							rootRubricPath,
							filtersValues,
							handleFilterChange,
							handleFilterReset
						}}
						showSortFilter={!isMapOpen}
					/>
				</Container>
			</LayoutBlock>
			{!_.isEmpty(rubricsTree.children) ? (
				<LayoutBlock gutter="mediumConstant">
					<Container>
						<Rubricator
							{...{
								rubricsTree,
								setFilter: handleFilterChange,
								rootRubricPath,
								currentRubricPath: rubric?.path
							}}
						/>
					</Container>
				</LayoutBlock>
			) : null}
			<LayoutBlock>
				<Container>
					<div ref={catalogContainerRef} className={styles.Catalog}>
						{isMapOpen ? (
							<Map
								items={mapItems}
								itemsLoading={mapItemsLoading}
								locale={urlParams.location}
								width={catalogContainerRef?.current?.offsetWidth}
								entityType="institutes"
								{...{ emptyMessage, rubric }}
							/>
						) : (
							<Catalog {...{ institutes, emptyMessage }} />
						)}
					</div>
				</Container>
			</LayoutBlock>
		</Layout>
	);
};

InstitutesTemplate.propTypes = {
	defaultUrlParams: PropTypes.object.isRequired,
	institutes: PropTypes.shape({
		pagination: PaginationPropType.isRequired,
		total: PropTypes.number.isRequired,
		items: PropTypes.array.isRequired
	}),
	menu: NavigationPropType.isRequired,
	meta: MetaPropType.isRequired,
	rubric: RubricPropType.isRequired,
	rubricsTree: RubricTreePropType.isRequired,
	urlParams: PropTypes.object.isRequired,
	rootRubricPath: PropTypes.string,
	federalSubjects: PropTypes.array,
	topPromoBlock: PromoBlockPropType,
	blocks: PropTypes.object,
	rubricName: PropTypes.string,
	filtersValues: filtersValuesPropType
};

export default withYMapsContext(InstitutesTemplate);
